import { CSSProperties, memo, ReactNode } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { messagesSelectors } from 'store/messages'
import { useModalActions } from 'providers/ModalProvider'
import { useGetSetting } from 'api/settings'
import clsx from 'clsx'

interface ChatBottomBarProps {
  children: ReactNode
  /** 'widget' = glass bar (default). 'page' = solid page background, seamless on the fullscreen /chat layout. */
  surface?: 'widget' | 'page'
}

const surfaceStyles: Record<NonNullable<ChatBottomBarProps['surface']>, CSSProperties> = {
  widget: {
    backdropFilter: 'blur(24px)',
    WebkitBackdropFilter: 'blur(24px)',
    background: 'var(--surface-glass-heavy)',
    borderTop: '1px solid var(--border-default)',
    boxShadow: '0 -6px 16px -8px rgba(0, 0, 0, 0.12)',
  },
  page: {
    background: 'var(--page-bg)',
  },
}

const linkClass = 'tw-bg-transparent tw-p-0 tw-align-baseline tw-underline tw-transition-opacity hover:tw-opacity-70'

const ChatBottomBarComponent = ({ children, surface = 'widget' }: ChatBottomBarProps) => {
  const disableFields = useSelector(messagesSelectors.disableFieldsTicketClosed)
  const { t } = useTranslation()
  const { showCookiePreferences } = useModalActions()
  const { setting } = useGetSetting()
  const privacyLink = setting?.privacyPolicyLink?.trim()
  const cookieLink = setting?.cookiePolicyLink?.trim()

  return (
    <div
      className={clsx(
        'tw-shrink-0 tw-z-10 tw-px-[12px] tw-pt-[12px] tw-pb-[4px]',
        disableFields ? 'tw-opacity-50 tw-blur-[1px]' : '',
      )}
      style={surfaceStyles[surface]}
    >
      {children}
      {/* EU AI Act art. 50 disclosure + policy links: one ultra-compact line to minimise height. */}
      <p
        className="tw-mt-1 tw-mb-0 tw-flex tw-flex-wrap tw-items-center tw-justify-center tw-gap-x-1 tw-text-[10px] tw-leading-none"
        style={{ color: 'var(--text-tertiary)' }}
      >
        <span>{t('aiDisclaimer')}</span>
        {privacyLink && (
          <>
            <span aria-hidden>·</span>
            <a href={privacyLink} target="_blank" rel="noreferrer" className={linkClass} style={{ color: 'inherit' }}>
              {t('chatLinks.privacy')}
            </a>
          </>
        )}
        {cookieLink && (
          <>
            <span aria-hidden>·</span>
            <a href={cookieLink} target="_blank" rel="noreferrer" className={linkClass} style={{ color: 'inherit' }}>
              {t('chatLinks.cookie')}
            </a>
            <span aria-hidden>·</span>
            <button
              type="button"
              onClick={() => showCookiePreferences()}
              className={linkClass}
              style={{ color: 'inherit' }}
            >
              {t('chatLinks.preferences')}
            </button>
          </>
        )}
      </p>
    </div>
  )
}

export const ChatBottomBar = memo(ChatBottomBarComponent)
ChatBottomBar.displayName = 'ChatBottomBar'
